import type { LabelWithField } from '@/components/ui/swimlane/types';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  ISwimlaneHeader,
  ISwimlaneHeaderProps,
  IVisualizationHeader,
  IVisualizationHeaderProps,
} from '@/lib/api/types/loop-visualization';
import { getHeaderFieldId, getHeaderItemKey } from './visualizationHeaderUtils';

type ItemWithChildren = ItemV2 & { children?: ItemWithChildren[] };

export function getItemValueByKey(
  item: ItemV2,
  key: keyof ItemV2 | `customFields.${string}` | null,
): unknown {
  if (!key) return null;
  if (key.startsWith('customFields.')) {
    const fieldId = key.slice('customFields.'.length);
    return item.customFieldValues?.[fieldId];
  }
  return item[key as keyof ItemV2];
}

export function toStringId(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value) && value.length > 0) return toStringId(value[0]);
  if (typeof value === 'object' && 'value' in value) {
    const inner = (value as { value: unknown }).value;
    return inner != null ? String(inner) : '';
  }
  return '';
}

export const resolveLabels = (
  rawValue: unknown,
  fieldName: string | null,
  headersMap: Map<string, ISwimlaneHeaderProps | IVisualizationHeaderProps>,
): LabelWithField[] => {
  if (rawValue == null || !fieldName) return [];
  const values = Array.isArray(rawValue) ? rawValue : [rawValue];
  const seen = new Set<string>();
  const result: LabelWithField[] = [];
  for (const v of values) {
    const id = toStringId(v);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const header = headersMap.get(id);
    const label = header?.label ?? id;
    result.push({
      fieldName,
      value: label,
      displayValues: header && 'displayValues' in header ? header.displayValues : undefined,
    });
  }
  return result;
};

const headersToMap = <T extends { id: string | number }>(headers: T[]): Map<string, T> => {
  const map = new Map<string, T>();
  for (const h of headers) {
    map.set(h.id.toString(), h);
  }
  return map;
};

export function buildItemLabelsMap({
  items,
  labelByHeader1,
  labelByHeader2,
  labelBy1Headers,
  labelBy2Headers,
}: {
  items: ItemWithChildren[];
  labelByHeader1: ISwimlaneHeader | IVisualizationHeader | null;
  labelByHeader2: ISwimlaneHeader | IVisualizationHeader | null;
  labelBy1Headers: IVisualizationHeaderProps[];
  labelBy2Headers: IVisualizationHeaderProps[];
}): Map<number, LabelWithField[]> {
  const field1 = getHeaderFieldId(labelByHeader1);
  const key1 = getHeaderItemKey(field1);
  const map1 = headersToMap(labelBy1Headers);

  const field2 = getHeaderFieldId(labelByHeader2);
  const key2 = getHeaderItemKey(field2);
  const map2 = headersToMap(labelBy2Headers);

  const result = new Map<number, LabelWithField[]>();
  const addItem = (item: ItemWithChildren) => {
    const labels = [
      ...resolveLabels(getItemValueByKey(item, key1), field1, map1),
      ...resolveLabels(getItemValueByKey(item, key2), field2, map2),
    ];
    if (labels.length > 0) {
      result.set(item.id, labels);
    }
    item.children?.forEach(addItem);
  };
  items.forEach(addItem);
  return result;
}
